import { h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import AIReviewDotRatingWidget
    from "../../../apps/containers/AppContainer/BarrelContainer/InspectableContainer/InspectableSidebarContainer/AIReviewsView/AIReviewScreenItems/AIReviewDotRatingWidget";
import RemarksContent
    from "../../../apps/containers/AppContainer/BarrelContainer/InspectableContainer/InspectableSidebarContainer/ResourceView/InfoTab/RemarksViews/RemarksContent";
import AIReviewLoading
    from "../../../apps/containers/AppContainer/BarrelContainer/InspectableContainer/InspectableViewContainer/InspectableWidgets/AIReviewWidget/AIReviewLoading";
import TextArea from "../../../apps/containers/AppContainer/TextArea";
import UserAvatar from "../../../apps/containers/UserAvatar";
import { cancelledStateResponse, loadingStateResponse } from "../../../apps/data/aiTweak/constants";
import InspectableViewActions from "../../../apps/data/inspectableView/InspectableViewActions";
import { formatTextArea } from "../../../apps/legacy/richText";
import { AITweak } from "../../../foundation/api/model";
import AIReviewDotRating from "../../../foundation/api/model/AIReview/AIReviewDotRating";
import {
    AITweakInteraction,
    AITweakInteractionStatus,
    AITweakSource,
    AITweakStatus
} from "../../../foundation/api/model/AITweak/AITweak";
import { userPrefs } from "../../../foundation/prefs";
import classname from "../../../foundation/utils/classname";
import { isMacOS } from "../../../foundation/utils/ua-utils";
import useDialogControls from "../../hooks/useDialogControls";
import IconAI from "../Icons/IconAI";
import IconChatHistory from "../Icons/IconChatHistory";
import IconChatWithoutHistory from "../Icons/IconChatWithoutHistory";

import Meter from "../Meter";

import { Props } from "./AITweakProps";

import { useScrollToLastLine } from "./useScrollToLastLine";

import styles from "./AITweak.module.css";

const CREDIT_PILL_ENABLED = false;
const LAST_THREE = -3;
const CHIP_MAX = 200;
const chipValue = 102;
const CLEANUP_DELAY = 50;
const quickActionsLines = [
    "Exclude all casing warnings",
    "Ignore small padding inconsistencies under 2px",
    "Ignore contrast issues inside disabled elements"
];
const randomLoadingTexts = [
    "Thinking…",
    "Mulling…",
    "Pondering…",
    "Considering…",
    "Analyzing…",
    "Reviewing…",
    "Evaluating…",
    "Inspecting…",
    "Examining…",
    "Digesting…",
    "Studying…",
    "Assessing…",
    "Deliberating…"
];
const randomTextMap = new Map();

function initVariables(aiTweakData: AITweak | null): {
    aiTweakLoading: boolean;
    chatLines: AITweakInteraction[];
} {
    const chatStatus = aiTweakData?.status ?? [];
    const aiTweakLoading = chatStatus === AITweakStatus.IN_PROGRESS ||
        chatStatus === AITweakStatus.PENDING;
    const isCancelled = aiTweakData?.interactions
        ?.[aiTweakData?.interactions?.length - 1]?.status === AITweakInteractionStatus.CANCELED;
    const chatLines = [
        ...aiTweakData?.interactions ?? [],
        aiTweakLoading ? loadingStateResponse : null,
        isCancelled ? cancelledStateResponse : null
    ].filter(Boolean) as AITweakInteraction[];

    return {
        aiTweakLoading,
        chatLines
    };
}

function AITweak({
    zoomLevel,
    aiTweakData,
    createAITweakInteraction,
    getProjectAITweak,
    saveAITweakChatHistory,
    setAITweakChatRating,
    stopAITweak
}: Props) {
    const [isHistoryEmpty, setIsHistoryEmpty] = useState((aiTweakData?.interactions ?? []).length === 0);
    const [aiTweakOpen, setAITweakOpen] = useState(false);
    const [inputText, setInputText] = useState("");
    const [savedUserChatHistory, setSavedUserChatHistory] = useState([]);
    const dialogRef = useRef<HTMLDivElement>(null);
    const { aiTweakLoading, chatLines = [] } = initVariables(aiTweakData);

    useScrollToLastLine<AITweakInteraction>([...chatLines].pop()!, aiTweakOpen);

    useDialogControls(dialogRef, {
        isOpen: aiTweakOpen,
        onClose: toggleAITweak(false),
        onOpen: toggleAITweak(true)
    });

    useEffect(() => {
        if (!aiTweakData) {
            getProjectAITweak(async (chatCount: number) => {
                setIsHistoryEmpty(chatCount === 0);

                if (chatCount === 0) {
                    await getSavedChatHistory();
                }
            });
        }
    }, []);

    async function getSavedChatHistory() {
        const savedChatHistory = await userPrefs.get("aiTweakChatHistory") ?? [];

        setSavedUserChatHistory(savedChatHistory);
    }

    function handleUpdateRating(chatLineId: string, currentRating?: AIReviewDotRating) {
        return (newRating: AIReviewDotRating) => {
            if (currentRating === newRating) {
                return;
            }

            setAITweakChatRating(chatLineId, newRating);
        };
    }

    function saveGivenInputIfNecessary() {
        if (isHistoryEmpty) {
            saveAITweakChatHistory(inputText);
        }
    }

    function handleStopAITweak(evt?: Event) {
        evt?.stopPropagation();
        evt?.preventDefault();

        if (
            aiTweakData?.status === AITweakStatus.IDLE ||
            chatLines?.[chatLines?.length - 1]._id === cancelledStateResponse._id
        ) {
            return;
        }

        stopAITweak();
    }

    function speakWithAITweak(chatText: string) {
        createAITweakInteraction(chatText);
        saveGivenInputIfNecessary();

        setTimeout(() => {
            setInputText("");
            setIsHistoryEmpty(false);
        }, CLEANUP_DELAY);
    }

    function handleSubmit(_v: string, e?: Event) {
        if (aiTweakLoading) {
            e?.stopPropagation();
            e?.preventDefault();
            return;
        }

        speakWithAITweak(inputText);
    }

    function renderEmptyActions() {
        const quickActionsToRender = savedUserChatHistory?.length
            ? savedUserChatHistory?.slice(LAST_THREE)
            : quickActionsLines;

        return (
            <div class={styles.aiTweakActionsArea}>
                <div class={classname(styles.quickActionsHeader, "body-1 medium")}>
                    <span>Want to make a change or ask a question?</span>
                    {// TOOD: Ali -> this is disabled for now
                        CREDIT_PILL_ENABLED && (
                            <div className={styles.creditChip}>
                                <span className="caption-2 semibold">{`${CHIP_MAX - chipValue} credit left`}</span>
                                <Meter max={CHIP_MAX} value={chipValue}/>
                            </div>
                        )
                    }
                </div>
                {
                    quickActionsToRender.map(actionRow => (
                        <div
                            key={actionRow}
                            class={classname(styles.quickActionsRow, "body-1")}>
                            {
                                savedUserChatHistory?.length
                                    ? <IconChatHistory/>
                                    : <IconChatWithoutHistory/>
                            }
                            <div
                                class="ellipsis body-1 regular"
                                title={actionRow}
                                onClick={handleQuickActionClick(actionRow)}>
                                {actionRow}
                            </div>
                        </div>
                    ))
                }
            </div>
        );
    }

    function renderChatContent() {
        return (
            <div class={styles.aiTweakChatArea}>
                <div class={styles.spacer}/>
                {
                    chatLines.map((chatLine, idx) => {
                        let {
                            _id: chatLineId,
                            message: chatText,
                            source: chatSource,
                            status: chatStatus,
                            rating: chatLineRating
                        } = chatLine;

                        const isLoading = chatStatus === AITweakInteractionStatus.IN_PROGRESS ||
                            chatStatus === AITweakInteractionStatus.PENDING;
                        const isCancelled = chatLineId === cancelledStateResponse._id;
                        const isAIAnswer = chatSource === AITweakSource.AGENT;
                        const isUserText = chatSource === AITweakSource.USER;
                        const chatLineClass = classname(styles.chatLine, {
                            [styles.aiChatLine]: isAIAnswer,
                            [styles.loading]: isLoading && chatLineId === loadingStateResponse._id,
                            [styles.cancelled]: isCancelled,
                            [styles.userChatLine]: isUserText
                        });

                        if (chatLineId === loadingStateResponse._id) {
                            const alreadyHaveLoadingText = randomTextMap.get(chatLines[idx - 1]._id);

                            if (!alreadyHaveLoadingText) {
                                chatText = randomLoadingTexts[Math.floor(Math.random() * 13)];
                                randomTextMap.set(chatLines[idx - 1]._id, chatText);
                            } else {
                                chatText = alreadyHaveLoadingText;
                            }

                            loadingStateResponse.message = chatText!;
                        }

                        return (
                            <div
                                class={chatLineClass}
                                id={chatLineId}
                                key={chatLineId}>
                                {
                                    isAIAnswer ? (
                                        <IconAI
                                            size={24}
                                            animate={isLoading}/>
                                    ) : (
                                        <div class={styles.chatUserProfile}>
                                            <UserAvatar user={window.Zeplin.user} size={24}/>
                                        </div>
                                    )
                                }
                                <div class={styles.chatTextWrapper}>
                                    <RemarksContent content={chatText}/>
                                    {
                                        isAIAnswer && !isLoading && !isCancelled && (
                                            <AIReviewDotRatingWidget
                                                class={styles.chatResponseRate}
                                                rating={chatLineRating}
                                                onChange={handleUpdateRating(chatLineId!, chatLineRating)}/>
                                        )
                                    }
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        );
    }

    function renderChatInput() {
        return (
            <TextArea
                containerClass={styles.aiTweakTextAreaContainer}
                class={styles.aiTweakTextArea}
                formatter={formatTextArea}
                placeholder="Request a change or ask a question…"
                maxLength={4096}
                hideCounter
                autoEdit
                disableShortcut={aiTweakLoading}
                buttonLabel={aiTweakLoading ? "Pause" : "Send"}
                enableSubmitButton={aiTweakLoading}
                disabledWhenEmpty={!aiTweakLoading}
                disabled={aiTweakLoading}
                value={inputText}
                editing={aiTweakOpen && !aiTweakLoading}
                submitOnEnter
                onInput={handleInput}
                onSubmit={handleSubmit}
                onCancelEdit={handleStopAITweak}/>
        );
    }

    function renderAITweakToggle() {
        if (aiTweakLoading) {
            return (
                <div class={styles.aiTweakWidget} onClick={toggleAITweak(!aiTweakOpen)}>
                    <AIReviewLoading
                        loadingText={loadingStateResponse.message}
                        onStop={handleStopAITweak}/>
                </div>
            );
        }

        return (
            <div class={styles.aiTweakWidget} onClick={toggleAITweak(!aiTweakOpen)}>
                <IconAI/>
                <div class={classname(styles.aiTweakText, "body-1 regular")}>
                    Request a change or ask a question…
                </div>
                <div class={classname(styles.aiTweakShortcut, "caption-2")}>
                    <div class={styles.shortcutSquare}>
                        {isMacOS() ? "⌘" : "Ctrl"}
                    </div>
                    <div>+</div>
                    <div style={{ width: "10px" }} class={styles.shortcutSquare}>/</div>
                </div>
            </div>
        );
    }

    function handleQuickActionClick(actionText: string) {
        return () => {
            speakWithAITweak(actionText);

            window.setTimeout(() => {
                document.getElementsByTagName("textarea")?.[0]?.focus();
            }, 50);
        };
    }

    function handleInput(value: string) {
        setInputText(value);
    }

    function toggleAITweak(value: boolean) {
        return () => {
            setAITweakOpen(value);
            setTimeout(() => {
                InspectableViewActions.changeZoomLevel(zoomLevel + zoomLevel * 0.001);
            }, 4);
        };
    }

    if (!aiTweakOpen) {
        return renderAITweakToggle();
    }

    return (
        <div id="AI_TWEAK_CHAT" class={styles.aiTweakChatContainer} ref={dialogRef}>
            {
                (isHistoryEmpty && !chatLines.length)
                    ? renderEmptyActions()
                    : renderChatContent()
            }
            {renderChatInput()}
        </div>
    );
}

export default AITweak;
